import React, { useRef, useState } from 'react';
import { PinturaEditor } from '@pqina/react-pintura';
import { getEditorDefaults } from '@pqina/pintura';
import '@pqina/pintura/pintura.css';
import { OrderImage } from 'lib/orders/images';
import { Layout, LayoutImageConfig } from 'lib/types/layout';
import { Modal } from 'lib/components/Modal';
import { uploadFilesToStorage } from 'lib/frontend/hooks/useFirebaseStorageUpload';
import Firebase from 'EnoticeFirebase';
import { ColumnService } from 'lib/services/directory';
import { getErrorReporter } from 'lib/utils/errors';
import { cdnIfy } from 'lib/helpers';
import { getBooleanFlag } from 'utils/flags';
import { LaunchDarklyFlags } from 'lib/types/launchDarklyFlags';
import { LayoutModel } from 'lib/model/objects/layoutModel';

/**
 * The width of the image to use for the editor, always use 800
 * We set a fixed width as otherwise small images are hard to see within the editor
 */
const FIXED_WIDTH_IMAGE_SIZE = 450;

type EditorUtil = 'crop';
const ENABLED_EDITOR_UTILS: EditorUtil[] = ['crop'];

const CUSTOM_EDITOR_TAB_TITLES = {
  crop: 'Drag the corners of the black grid to change how your image is cropped'
};

type ImageEditorModalProps = {
  onUpdateOrderImage: (orderImage: OrderImage) => void;
  orderImage: OrderImage;
  imageConfig: LayoutImageConfig;
  onClose: () => void;
  layout: Layout;
  disabled?: boolean;
  uploadFolder: string;
};

export default function AdvancedImageEditorModal({
  onUpdateOrderImage,
  orderImage,
  imageConfig,
  onClose,
  layout,
  disabled,
  uploadFolder
}: ImageEditorModalProps) {
  const layoutModel = new LayoutModel(layout);
  const editorRef = useRef<PinturaEditor>(null);
  const [uploading, setUploading] = useState(false);
  const [selectedTab, setSelectedTab] = useState<EditorUtil>('crop');

  const fixedWidthOrderURL = cdnIfy(orderImage.sourcePath, {
    imgixTransformations: { w: `${FIXED_WIDTH_IMAGE_SIZE}` },
    useImgix: true
  });

  /**
   * Save the edits to the image, upoload to storage and override the order image
   */
  const handleSaveEdits = async () => {
    const currentFile = editorRef.current?.editor.imageFile;
    if (!currentFile) {
      return;
    }
    const processedImage = await editorRef.current?.editor.processImage();
    const useColumnCDN = getBooleanFlag(LaunchDarklyFlags.ENABLE_COLUMN_CDN);
    if (!processedImage) return;
    setUploading(true);
    const newFileName = `edited-${new Date().getTime()}-${currentFile.name}`;

    const newFile = new File([processedImage.dest as File], newFileName, {
      type: currentFile.type
    });
    const { successfulFilesAndUploads } = await uploadFilesToStorage(
      Firebase.storage(),
      [newFile],
      uploadFolder
    );
    if (successfulFilesAndUploads.length > 0) {
      const [successfulFileAndUpload] = successfulFilesAndUploads;
      onUpdateOrderImage({
        ...orderImage,
        sourcePath: successfulFileAndUpload.uploadRef.fullPath,
        imageUrl: cdnIfy(successfulFileAndUpload.uploadRef.fullPath, {
          cloudinaryTransformations: 'q_auto:best',
          useColumnCDN
        })
      });
    } else {
      getErrorReporter().logAndCaptureError(
        ColumnService.FILE_STORAGE,
        new Error('Failed to upload file'),
        'Failed to upload file'
      );
    }
    setUploading(false);
    onClose();
  };

  return (
    <Modal
      title={'Edit Image'}
      id="edit-image-modal"
      onClose={onClose}
      primaryAction={{
        buttonText: 'Save Edits',
        onClick: handleSaveEdits,
        type: 'button'
      }}
      size="3xl"
      loading={uploading}
    >
      <div className="flex flex-col gap-4">
        <div className="text-column-gray-400 text-center font-semibold">
          {CUSTOM_EDITOR_TAB_TITLES[selectedTab]}
        </div>
        <div style={{ height: '40vh' }}>
          <PinturaEditor
            {...getEditorDefaults()}
            src={fixedWidthOrderURL}
            utils={ENABLED_EDITOR_UTILS}
            util={selectedTab}
            onSelectutil={newTab => setSelectedTab(newTab as EditorUtil)}
            imageCropAspectRatio={layoutModel.imageAspectRatioDecimal(
              imageConfig
            )}
            ref={editorRef}
            enableButtonExport={false}
            disabled={disabled}
          />
        </div>
      </div>
    </Modal>
  );
}
